import type {
  AnalysisSpeedData,
  AnalysisSpeedReport,
  LighthouseDetails
} from '@/app/PageSpeed/api/types/analysisApiTypes';

export type SpeedStrategy = 'mobile' | 'desktop';

export type SpeedReportRow = {
  strategy: SpeedStrategy;
  details?: LighthouseDetails;
  created_at?: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

export const isLighthouseDetails = (
  value: unknown
): value is LighthouseDetails =>
  isRecord(value) && isRecord(value.categories);

const asStrategy = (value: unknown): SpeedStrategy | undefined =>
  value === 'mobile' || value === 'desktop' ? value : undefined;

const uniqueStrategies = (values: SpeedStrategy[]): SpeedStrategy[] =>
  Array.from(new Set(values));

const inferStrategy = (details?: LighthouseDetails): SpeedStrategy | undefined => {
  const settings = isRecord(details?.configSettings)
    ? details.configSettings
    : undefined;
  const form =
    asStrategy(settings?.formFactor) || asStrategy(settings?.emulatedFormFactor);
  if (form) return form;
  const screen = isRecord(settings?.screenEmulation)
    ? settings.screenEmulation
    : undefined;
  if (screen?.mobile === true) return 'mobile';
  if (screen?.mobile === false) return 'desktop';
  return undefined;
};

const rowFromReport = (row: AnalysisSpeedReport): SpeedReportRow => {
  const details = isLighthouseDetails(row.details)
    ? row.details
    : isLighthouseDetails(row)
      ? row
      : undefined;
  return {
    strategy: asStrategy(row.strategy) || inferStrategy(details) || 'mobile',
    details,
    created_at: row.created_at
  };
};

export const listSpeedReportRows = (
  data?: AnalysisSpeedData | null
): SpeedReportRow[] => {
  if (!data) return [];
  if (Array.isArray(data.reports) && data.reports.length) {
    return data.reports.map(rowFromReport);
  }
  if (Array.isArray(data.details)) {
    return data.details.filter(isLighthouseDetails).map(details => ({
      strategy: inferStrategy(details) || 'mobile',
      details
    }));
  }
  if (isLighthouseDetails(data.details)) {
    return [
      {
        strategy:
          asStrategy(data.strategy) ||
          inferStrategy(data.details) ||
          'mobile',
        details: data.details,
        created_at: data.created_at
      }
    ];
  }
  return [];
};

export const listAvailableSpeedStrategies = (
  data?: AnalysisSpeedData | null
): SpeedStrategy[] => {
  if (!data) return [];
  const listed = (data.available_strategies || [])
    .map(asStrategy)
    .filter((value): value is SpeedStrategy => Boolean(value));
  if (listed.length) return uniqueStrategies(listed);
  if (
    data.device_type === 'both' ||
    data.settings?.device_type === 'both'
  ) {
    return ['mobile', 'desktop'];
  }
  const fromRows = listSpeedReportRows(data).map(row => row.strategy);
  if (fromRows.length) return uniqueStrategies(fromRows);
  const single = asStrategy(data.strategy) || asStrategy(data.device_type);
  return [single || 'mobile'];
};

export const pickSpeedDetails = (
  data: AnalysisSpeedData | null | undefined,
  strategy: SpeedStrategy
): { details?: LighthouseDetails; created_at?: string } => {
  if (isLighthouseDetails(data?.details)) {
    return { details: data.details, created_at: data?.created_at };
  }
  const rows = listSpeedReportRows(data);
  const selected =
    rows.find(row => row.strategy === strategy) ||
    rows.find(row => row.strategy === 'mobile') ||
    rows[0];
  return {
    details: selected?.details,
    created_at: selected?.created_at
  };
};
